import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Empty, Spin } from 'antd'
import { useHistory } from 'react-router-dom'
import { getAicMySpaceProjects } from '../../request'
import { formatDateTimeMinute, termDisplay, unwrapRows } from './terminalModel'

const DATASETS_PAGE = '/portal/aic-dashboard/v2/datasets'

// 项目接入状态文案与个人中心保持一致（Profile.js renderStatusTag）。
const PROJECT_STATUS_META = {
  approved: { label: '已创建', tone: 'muted' },
  provisioning: { label: '接入中', tone: 'warn' },
  ready: { label: '已就绪', tone: 'ok' },
  failed: { label: '接入失败', tone: 'danger' },
  deleting: { label: '删除中', tone: 'danger' },
  disabled: { label: '已停用', tone: 'muted' }
}

// 数据集健康口径与看板详情一致（terminalModel.healthLabel）。
const HEALTH_META = {
  healthy: { label: '正常', tone: 'ok' },
  warning: { label: '异常', tone: 'warn' },
  failing: { label: '故障', tone: 'danger' },
  unknown: { label: '未知', tone: 'muted' }
}

const statusMetaOf = status => PROJECT_STATUS_META[status] || { label: status || '未知', tone: 'muted' }

/**
 * V2 总览「我的项目」列表（P4）：只展示本人作为登记人的项目。
 * AIC 数据集行跳 V2 数据源页（带 projectId，由 TerminalLedger 自动打开详情）；
 * Vibe 应用行本轮仍深链个人中心（个人中心 tab 暂不下线）。
 * 数据来自 /aic/profile/space/projects；失败只降级本列表。
 */
export default function MyProjectsList ({ reloadToken = 0 }) {
  const history = useHistory()
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const loadProjects = useCallback(() => {
    setLoading(true)
    setError('')
    return getAicMySpaceProjects({})
      .then(result => {
        if (!mountedRef.current) return
        setProjects(unwrapRows(result))
        setLoading(false)
      })
      .catch(() => {
        if (!mountedRef.current) return
        setLoading(false)
        setError('我的项目加载失败')
      })
  }, [])

  useEffect(() => {
    loadProjects()
    // reloadToken 由新建项目成功后的上层回调递增，用于重新拉取列表。
  }, [loadProjects, reloadToken])

  // P6：AIC 数据集与 Vibe 应用行都进入 V2 看板详情（应用详情含「概览 + 操作」）；
  // 个人中心「Vibe 项目」tab 保留，但从这里不再深链过去。
  const openProject = project => {
    const projectId = project && project.projectId
    if (projectId === null || projectId === undefined || projectId === '') return
    history.push(`${DATASETS_PAGE}?projectId=${encodeURIComponent(projectId)}`)
  }

  return <div className="term-panel term-my">
    <div className="term-panel-title term-my-head">
      <span>我的项目</span>
      {!loading && !error ? <span className="term-my-count">{projects.length} 个</span> : null}
      <span className="term-my-hint">点击任意行进入看板详情（含操作 tab）；应用项目也支持在看板内维护</span>
    </div>

    {error
      ? <Alert
        type="error"
        showIcon
        message={error}
        description="仅影响我的项目列表，空间卡片和其它总览内容不受影响。"
        action={<button type="button" className="term-link" onClick={loadProjects}>重试</button>}
      />
      : loading
        ? <div className="term-space-loading"><Spin size="small" /> 加载我的项目…</div>
        : projects.length === 0
          ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无项目" />
          : <table className="term-table term-table-plain term-my-table">
            <thead>
              <tr>
                <th>项目</th>
                <th>类型</th>
                <th>状态</th>
                <th>数据集健康</th>
                <th>最近上传</th>
                <th>下次上传</th>
              </tr>
            </thead>
            <tbody>
              {projects.map(project => {
                const dataset = project.projectKind === 'dataset'
                const statusMeta = statusMetaOf(project.status)
                const healthMeta = HEALTH_META[project.healthStatus] || null
                return <tr
                  key={`${project.projectKind || 'application'}-${project.projectId}-${project.projectName || ''}`}
                  className="term-row term-my-row"
                  title={dataset ? '查看数据源详情（含操作 tab）' : '查看应用项目详情（含操作 tab）'}
                  onClick={() => openProject(project)}
                >
                  <td className="term-my-project">
                    <span className="term-my-project-name">{termDisplay(project.projectName, '未命名项目')}</span>
                    {project.description
                      ? <span className="term-my-project-desc" title={termDisplay(project.description)}>{project.description}</span>
                      : null}
                  </td>
                  <td className="term-nowrap">
                    <span className={`term-src ${dataset ? 'term-src-aic' : 'term-src-vibe'}`}>{dataset ? 'AIC' : 'Vibe'}</span>
                  </td>
                  <td className="term-nowrap">
                    <span className={`term-my-status term-my-tone-${statusMeta.tone}`}>{statusMeta.label}</span>
                  </td>
                  <td className="term-nowrap">
                    {dataset
                      ? (healthMeta
                        ? <span className={`term-my-status term-my-tone-${healthMeta.tone}`}>{healthMeta.label}</span>
                        : <span className="term-muted">—</span>)
                      : <span className="term-muted">—</span>}
                  </td>
                  <td className="term-nowrap">
                    {dataset
                      ? (project.lastSuccessfulUploadAt
                        ? formatDateTimeMinute(project.lastSuccessfulUploadAt)
                        : <span className="term-muted">从未上传</span>)
                      : <span className="term-muted">—</span>}
                  </td>
                  <td className="term-nowrap">
                    {dataset
                      ? (project.nextExpectedUploadAt
                        ? formatDateTimeMinute(project.nextExpectedUploadAt)
                        : <span className="term-muted">未配置</span>)
                      : <span className="term-muted">—</span>}
                  </td>
                </tr>
              })}
            </tbody>
          </table>}
    <div className="term-foot">项目清单来自 AIC 控制面（仅本人登记的项目）；健康只按上传及时性判定，不代表数据质量。</div>
  </div>
}
